import { IconFile, IconUpload, IconX } from "@tabler/icons-react";
import { useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";
import { Button } from "./Button/Button";

type Props = {
  className?: string;
  onDrop: (files: File[] | null) => void;
  value?: File[] | null;
  multiple?: boolean;
  accept?: Record<string, string[]>;
  existingFileName?: string | null;
  onClearExisting?: () => void;
  isReplace?: boolean;
};

export default function FileUpload({
  onDrop,
  className,
  value,
  multiple = false,
  accept,
  existingFileName,
  onClearExisting,
  isReplace = false,
}: Props) {
  if (value && value.length > 0) {
    return (
      <FilePreview
        fileName={
          value.length === 1 ? value[0]!.name : `${value.length} files selected`
        }
        onClear={() => onDrop(null)}
        className={className}
      />
    );
  }

  if (existingFileName) {
    return (
      <ExistingFilePreview
        fileName={existingFileName}
        onReplace={(files) => onDrop(files)}
        onClear={onClearExisting}
        accept={accept}
        multiple={multiple}
        className={className}
      />
    );
  }

  return (
    <UploadInput
      onDrop={(files) => onDrop(files)}
      className={className}
      multiple={multiple}
      accept={accept}
      isReplace={isReplace}
    />
  );
}

function UploadInput({
  onDrop,
  className,
  multiple,
  accept,
  isReplace,
}: {
  onDrop: (files: File[]) => void;
  className?: string;
  multiple: boolean;
  accept?: Record<string, string[]>;
  isReplace: boolean;
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onDrop(acceptedFiles);
      }
    },
  });

  const backgroundColor = isDragActive ? "bg-primary-100" : "bg-file-upload";
  const borderColor = isDragActive ? "border-primary-400" : "border-border";

  return (
    <div
      className={cn(
        "relative flex flex-1 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed p-8 [&_svg]:stroke-[url(#icon-gradient)]",
        backgroundColor,
        borderColor,
        className,
      )}
      {...getRootProps()}
    >
      <input {...getInputProps()} className="h-full w-full" />

      <IconUpload stroke={2} size={24} />
      <div className="h-2"></div>
      <h1 className="text-sm font-semibold">
        {isReplace
          ? `Click to replace ${multiple ? "files" : "file"}`
          : `Click to upload ${multiple ? "files" : "file"}`}
      </h1>
      <p className="text-sm text-text-secondary">or drag and drop</p>
    </div>
  );
}

function FilePreview({
  fileName,
  onClear,
  className,
}: {
  fileName: string;
  onClear: () => void;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "relative flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed p-8",
        className,
      )}
    >
      <div className="flex items-center [&_svg]:stroke-[url(#icon-gradient)]">
        <IconFile stroke={2} size={24} className="mr-2 shrink-0" />
        <span className="line-clamp-1 text-sm font-medium text-text-primary">
          {fileName}
        </span>
      </div>

      <Button
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
        className="absolute top-2 right-2 cursor-pointer rounded-sm"
        aria-label="Remove files"
        variant="ghost"
        size="iconSm"
      >
        <IconX size={16} />
      </Button>
    </div>
  );
}

function ExistingFilePreview({
  fileName,
  onReplace,
  onClear,
  accept,
  multiple,
  className,
}: {
  fileName: string;
  onReplace: (files: File[]) => void;
  onClear?: () => void;
  accept?: Record<string, string[]>;
  multiple: boolean;
  className?: string;
}) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    multiple,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        onReplace(acceptedFiles);
      }
    },
  });

  return (
    <div
      className={cn(
        "relative flex flex-1 cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed p-8 transition-colors",
        isDragActive ? "border-primary-400 bg-primary-100" : "border-border",
        className,
      )}
      {...getRootProps()}
    >
      <input {...getInputProps()} className="h-full w-full" />

      <div className="flex items-center [&_svg]:stroke-[url(#icon-gradient)]">
        <IconFile stroke={2} size={24} className="mr-2 shrink-0" />
        <span className="line-clamp-1 text-sm font-medium text-text-primary">
          {fileName}
        </span>
      </div>
      <p className="mt-2 text-xs text-text-secondary">
        Click or drag a file to replace
      </p>

      {onClear && (
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          className="absolute top-2 right-2 cursor-pointer rounded-sm"
          aria-label="Remove file"
          variant="ghost"
          size="iconSm"
        >
          <IconX size={16} />
        </Button>
      )}
    </div>
  );
}
